import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useBilling } from "@/hooks/useBilling";
import TrialBanner from "@/components/dashboard/TrialBanner";
import {
  LayoutDashboard,
  List,
  Briefcase,
  Users,
  Ban,
  Eye,
  Settings,
  LogOut,
  Menu,
  Gift,
  BarChart3,
  UserCheck,
} from "lucide-react";
import SlotifyLogo from "@/components/SlotifyLogo";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";

const baseSidebarLinks = [
  { to: "/dashboard", icon: LayoutDashboard, labelKey: "nav.calendar", end: true },
  { to: "/dashboard/reservations", icon: List, labelKey: "nav.reservations" },
  { to: "/dashboard/services", icon: Briefcase, labelKey: "nav.services" },
  { to: "/dashboard/upsells", icon: Gift, labelKey: "nav.upsells" },
  { to: "/dashboard/customers", icon: Users, labelKey: "nav.customers" },
  { to: "/dashboard/blocked", icon: Ban, labelKey: "nav.blocked" },
  { to: "/dashboard/settings", icon: Settings, labelKey: "nav.settings" },
];

const SidebarContent = ({ onNavigate, sidebarLinks }: { onNavigate?: () => void; sidebarLinks: typeof baseSidebarLinks }) => {
  const { t } = useLanguage();
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();

  const initials = profile
    ? `${(profile.first_name || "")[0] || ""}${(profile.last_name || "")[0] || ""}`.toUpperCase() || "?"
    : "?";

  const handleSignOut = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await signOut();
    navigate("/login");
  };

  return (
    <>
      <div className="flex h-16 items-center border-b border-sidebar-border px-5">
        <div className="flex items-center gap-2 justify-between w-full pr-8 md:pr-2">
          <SlotifyLogo className="h-6" />
          <Link to={profile?.organisation_id ? `/shop/${profile.organisation_id}` : "#"} target="_blank" className="flex h-8 w-8 items-center justify-center rounded-lg text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors" title={t("nav.view_shop")}>
            <Eye className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <nav className="flex-1 space-y-1 p-3 mt-2">
        {sidebarLinks.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-sidebar-accent text-sidebar-primary"
                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
              }`
            }
          >
            <link.icon className="h-4 w-4" />
            {t(link.labelKey)}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-sidebar-border p-3">
        <Link
          to="/dashboard/settings/profile"
          onClick={onNavigate}
          className="flex items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-sidebar-accent"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-sidebar-primary/20 text-xs font-bold text-sidebar-primary">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-sidebar-foreground truncate">
              {profile ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || "User" : "Loading…"}
            </p>
            <p className="text-xs text-sidebar-foreground/50 truncate">{profile?.email || ""}</p>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent" onClick={handleSignOut}>
            <LogOut className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </>
  );
};

const DashboardLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isPastDue, isCancelled, isTrialing, isTrialExpired, isActive, cancelledButActive } = useBilling();
  const { profile } = useAuth();
  const location = useLocation();
  const [statsEnabled, setStatsEnabled] = useState(false);
  const [employeesEnabled, setEmployeesEnabled] = useState(false);

  const orgId = profile?.organisation_id;

  useEffect(() => {
    if (!orgId) return;
    const fetchExtensions = () => {
      supabase
        .from("organisation_extensions")
        .select("extension_id, enabled")
        .eq("organisation_id", orgId)
        .in("extension_id", ["statistics", "employees"])
        .then(({ data }) => {
          setStatsEnabled(!!data?.find((e: any) => e.extension_id === "statistics")?.enabled);
          setEmployeesEnabled(!!data?.find((e: any) => e.extension_id === "employees")?.enabled);
        });
    };
    fetchExtensions();
    const handler = () => fetchExtensions();
    window.addEventListener("extensions-changed", handler);
    return () => window.removeEventListener("extensions-changed", handler);
  }, [orgId]);

  const sidebarLinks = (() => {
    let links = [...baseSidebarLinks];
    const settingsIdx = links.findIndex((l) => l.labelKey === "nav.settings");
    const extras: typeof baseSidebarLinks = [];
    if (employeesEnabled) extras.push({ to: "/dashboard/employees", icon: UserCheck, labelKey: "nav.employees" });
    if (statsEnabled) extras.push({ to: "/dashboard/statistics", icon: BarChart3, labelKey: "nav.statistics" });
    links.splice(settingsIdx, 0, ...extras);
    return links;
  })();

  // When past_due or cancelled (and not trialing/active/within paid period), only allow settings/billing and profile
  const isBillingAllowed =
    location.pathname.startsWith("/dashboard/settings");

  const isBlocked = (isPastDue || isTrialExpired || (isCancelled && !isTrialing && !isActive && !cancelledButActive)) && !isBillingAllowed;

  return (
    <div className="flex h-screen bg-background">
      <aside className="hidden md:flex w-64 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
        <SidebarContent sidebarLinks={sidebarLinks} />
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex md:hidden h-14 items-center gap-3 border-b border-border px-4 bg-card">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 bg-sidebar border-none">
              <div className="flex h-full flex-col">
                <SidebarContent onNavigate={() => setMobileOpen(false)} sidebarLinks={sidebarLinks} />
              </div>
            </SheetContent>
          </Sheet>
          <SlotifyLogo className="h-5" />
        </header>

        <TrialBanner />

        <main className="flex-1 overflow-auto">
          {isBlocked ? (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-8 max-w-md">
                <h2 className="text-lg font-bold text-foreground mb-2">
                  {isPastDue ? "Account suspended" : isTrialExpired ? "Trial expired" : "Subscription cancelled"}
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  {isPastDue
                    ? "Your last payment failed. Please update your payment method to restore access to your dashboard."
                    : isTrialExpired
                    ? "Your free trial has expired. Please set up a subscription to continue using your dashboard."
                    : "Your subscription has been cancelled. Please reactivate your subscription to restore access to your dashboard."}
                </p>
                <Button asChild>
                  <Link to="/dashboard/settings/billing">Go to billing</Link>
                </Button>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
